/**
 * Sectors, as short codes.
 *
 * A sector name was stored three times over: on every holding, in every
 * per-record sector dictionary, and again as the name of each slice of a sector
 * allocation. "Hotels, Resorts & Cruise Lines" is thirty characters carried
 * twenty-four times.
 *
 * Mnemonics rather than positions. An index into an ordered list shifts every
 * later sector the moment one is inserted, which would silently re-label every
 * holding in the artefact; a mnemonic survives insertion and is legible in the
 * raw data.
 *
 * The names are the issuers' own and are GICS-derived, so they are carried as
 * published rather than tidied. A taxonomy of our own is a separate decision
 * from an encoding one.
 *
 * Generated once from the 114 distinct values on file, then maintained here.
 */
export const SECTORS: Record<string, string> = {
    ad: "Aerospace & Defense",
    afm: "Agricultural & Farm Machinery",
    afl: "Air Freight & Logistics",
    am: "Asset Management",
    amibb: "Asset Mgmt/Invt Bank & Brokers",
    ac: "Automobiles & Components",
    bank: "Banks",
    bese: "Battery & Energy Storage Equipment",
    biot: "Biotechnology",
    bh: "Bitcoin Holding",
    bm: "Bitcoin Miners",
    bts: "Bitcoin Trading & Services",
    cm: "Capital Markets",
    ce: "Cash Equivalent",
    ce2: "Cash Equivalents",
    cg: "Casinos & Gaming",
    cs: "Communication Services",
    comm: "Communications",
    ce3: "Communications Equipment",
    cmhte: "Construction Machinery & Heavy Transportation Equipment",
    cd: "Consumer Discretionary",
    cs2: "Consumer Staples",
    cm2: "Crypto Miners",
    ctw: "Crypto Trading/ Wallets",
    dcr: "Data Center REITs",
    dtl: "Deposit Taking / Lending",
    das: "Digital Assets & Services",
    dive: "Diversified",
    dr: "Diversified REITs",
    dts: "Diversified Telecommunication Services",
    eu: "Electric Utilities",
    ece: "Electrical Components & Equipment",
    eeic: "Electronic Equipment, Instruments & Components",
    ener: "Energy",
    efs: "Environmental & Facilities Services",
    equi: "Equities",
    fina: "Financials",
    fi: "Fixed Income",
    gu: "Gas Utilities",
    gold: "Gold",
    gb: "Government Bonds",
    hc: "Health Care",
    hces: "Health Care Equipment & Supplies",
    hcps: "Health Care Providers & Services",
    hcr: "Health Care REITs",
    heal: "Healthcare",
    hr: "Healthcare REITs",
    hee: "Heavy Electrical Equipment",
    hrr: "Hotel & Resort REITs",
    hrcl: "Hotels, Resorts & Cruise Lines",
    hfce: "Hydrogen & Fuel Cell Equipment",
    imsc: "Industrial Machinery & Supplies & Components",
    ir: "Industrial REITs",
    indu: "Industrials",
    it: "Information Technology",
    insu: "Insurance",
    iog: "Integrated Oil & Gas",
    ims: "Interactive Media & Services",
    lc: "Large Cap",
    lf: "Leisure Facilities",
    lsts: "Life Sciences Tools & Services",
    lt: "Long-Term",
    mate: "Materials",
    medi: "Media",
    me: "Media & Entertainment",
    mfrr: "Multi-Family Residential REITs",
    mu: "Multi-Utilities",
    or: "Office REITs",
    oges: "Oil & Gas Equipment & Services",
    ogep: "Oil & Gas Exploration & Production",
    ogrm: "Oil & Gas Refining & Marketing",
    ogst: "Oil & Gas Storage & Transportation",
    ogcf: "Oil, Gas & Consumable Fuels",
    othe: "Other",
    oes: "Other Equipment & Services",
    of: "Other Financials",
    osr: "Other Specialized REITs",
    pa: "Passenger Airlines",
    pgt: "Passenger Ground Transportation",
    phar: "Pharmaceuticals",
    pipe: "Pipelines",
    reit: "REITs",
    rt: "Rail Transportation",
    re: "Real Estate",
    reoc: "Real Estate Operating Companies",
    res: "Real Estate Services",
    rpg: "Renewable Power Generation",
    rr: "Retail REITs",
    ssr: "Self-Storage REITs",
    semi: "Semiconductors",
    ss: "Semiconductors & Semiconductor",
    sse: "Semiconductors & Semiconductor Equipment",
    st: "Short-Term",
    silv: "Silver",
    sfrr: "Single-Family Residential REIT",
    soft: "Software",
    ss2: "Software & Services",
    ses: "Solar Equipment & Services",
    tech: "Technology",
    th: "Technology Hardware",
    ths: "Technology Hardware, Storage &",
    thsp: "Technology Hardware, Storage & Peripherals",
    ttr: "Telecom Tower REITs",
    tele: "Telecoms",
    tcd: "Trading Companies & Distributors",
    tps: "Transaction Processing/ Staking",
    tb: "Treasury Bonds",
    ub: "US Banks",
    util: "Utilities",
    wu: "Water Utilities",
    wes: "Wind Equipment & Services",
    wts: "Wireless Telecommunication Services",
};

const BY_NAME: Record<string, string> = Object.fromEntries(Object.entries(SECTORS).map(([code, name]) => [name.toLowerCase(), code]));

export const sectorName = (code: string | null | undefined): string | null => (code ? (SECTORS[code] ?? code) : null);

/** Null for a name we have not seen, so the caller can carry it through as text
    rather than inventing a code the decoder would not know. */
export const codeForSector = (name: string | null | undefined): string | null => (name ? (BY_NAME[name.toLowerCase()] ?? null) : null);
